import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Plug, Layers, Coins, ArrowRight, ArrowDown, MonitorSmartphone, Network,
  FileText, Ticket, Mail, BarChart3, Wallet, Puzzle
} from 'lucide-react';
import platforms from '../data/platforms.json';
import { useLanguage } from '../contexts/LanguageContext';
import { tr } from '../utils/i18n';
import { routePath } from '../i18n/routes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* i18n:extract */
const benefits = [
  {
    icon: 'Plug',
    title: 'Une intégration unique',
    description: "Accédez aux plateformes disponibles via l'API OptimHub, sans construire une connexion distincte pour chacune."
  },
  {
    icon: 'Layers',
    title: "Davantage d'opportunités",
    description: 'Élargissez vos possibilités de monétisation selon votre audience, vos formats et les offres accessibles.'
  },
  {
    icon: 'Coins',
    title: 'CPC et CPA',
    description: 'Accédez à des opportunités rémunérées au clic ou à la conversion, selon les campagnes et les conditions proposées.'
  }
];

const steps = [
  {
    label: 'Étape 1',
    title: 'Présentez votre activité',
    description: 'Partagez votre site, votre application ou votre outil et les caractéristiques principales de votre audience.'
  },
  {
    label: 'Étape 2',
    title: 'Identifiez les opportunités adaptées',
    description: 'Notre équipe étudie avec vous les plateformes et modèles de rémunération pertinents.'
  },
  {
    label: 'Étape 3',
    title: "Intégrez l'API OptimHub",
    description: "Mettez en place la connexion avec l'accompagnement de notre équipe."
  }
];

const publisherTypes = [
  { icon: 'FileText', title: 'Médias et blogs', description: 'Contenus éditoriaux, tests produits, guides et actualités.' },
  { icon: 'Ticket', title: 'Sites de coupons', description: 'Codes promo, bons plans et offres à forte intention d\'achat.' },
  { icon: 'Mail', title: 'Newsletters', description: 'Audiences abonnées et placements natifs dans vos éditions.' },
  { icon: 'BarChart3', title: 'Comparateurs', description: 'Comparaison de prix, de produits ou de services.' },
  { icon: 'Wallet', title: 'Cashback', description: 'Programmes de cashback et de fidélité.' },
  { icon: 'Puzzle', title: 'Extensions navigateur', description: 'Assistants shopping, outils et extensions.' }
];

const publisherTypeOptions = [
  { value: 'blog', label: 'Média ou blog' },
  { value: 'coupon', label: 'Site de coupons' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'comparison', label: 'Comparateur' },
  { value: 'cashback', label: 'Cashback' },
  { value: 'extension', label: 'Extension navigateur' },
  { value: 'social', label: 'Communauté ou réseaux sociaux' },
  { value: 'other', label: 'Autre' }
];

const trafficOptions = [
  { value: 'under-50k', label: 'Moins de 50 000 visites / mois' },
  { value: '50k-250k', label: '50 000 - 250 000 visites / mois' },
  { value: '250k-1m', label: '250 000 - 1 million de visites / mois' },
  { value: '1m-5m', label: '1 - 5 millions de visites / mois' },
  { value: 'over-5m', label: 'Plus de 5 millions de visites / mois' }
];
/* i18n:end */

const icons = { Plug, Layers, Coins, FileText, Ticket, Mail, BarChart3, Wallet, Puzzle };

const emptyForm = {
  name: '', email: '', siteUrl: '', siteType: '',
  monthlyTraffic: '', countries: '', verticals: '', message: ''
};

const PublishersPage = () => {
  const { currentLanguage } = useLanguage();
  const l = (text) => tr(currentLanguage, text);
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState({ state: 'idle', message: '' });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ state: 'loading', message: '' });
    try {
      const response = await axios.post(`${BACKEND_URL}/api/publishers/apply`, {
        name: form.name,
        email: form.email,
        siteUrl: form.siteUrl,
        siteType: form.siteType,
        monthlyTraffic: form.monthlyTraffic || null,
        countries: form.countries || null,
        verticals: form.verticals || null,
        message: form.message || null,
        language: currentLanguage
      });
      setStatus({ state: 'success', message: response.data.message });
      setForm(emptyForm);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        l("L'envoi a échoué. Merci de vérifier les champs et de réessayer.");
      setStatus({ state: 'error', message });
    }
  };

  const labelClass = 'block text-sm font-medium text-gray-700 mb-2';
  const inputClass =
    'w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 transition-colors duration-200';

  return (
    <div className="min-h-screen bg-white" data-testid="publishers-page">
      <SEOHead
        title={l('Accès API éditeurs OptimHub — une seule API, plusieurs plateformes de monétisation')}
        description={l("Connectez votre site, votre application ou votre outil à OptimHub et accédez aux plateformes de monétisation disponibles via une API unique, en CPC et en CPA.")}
        keywords="API monétisation éditeurs, publisher API, CPC, CPA, plateformes d'affiliation, monétisation audience"
        routeKey="publishers"
        lang={currentLanguage}
      />
      <Header />

      {/* 1. Hero */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <Badge className="bg-blue-100 text-blue-800 mb-6" data-testid="publishers-eyebrow">
              <Network className="w-4 h-4 mr-2" />
              {l('Pour les éditeurs, médias et plateformes')}
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
              {l('Une seule API.')}
              <span className="block bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent mt-2">
                {l('De nombreuses plateformes pour monétiser votre audience.')}
              </span>
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              {l("Connectez votre site, votre application ou votre outil à OptimHub et accédez aux plateformes de monétisation disponibles via une API unique. Une intégration pour élargir vos opportunités, avec des modèles de rémunération au CPC et au CPA.")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                data-testid="publishers-hero-cta"
                onClick={() => scrollTo('publisher-contact')}
                className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-8 py-6 text-base rounded-full transition-all duration-300 transform hover:scale-105"
              >
                {l('Demander mon accès API')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                data-testid="publishers-hero-secondary"
                variant="outline"
                onClick={() => scrollTo('publisher-platforms')}
                className="px-8 py-6 text-base rounded-full border-gray-300"
              >
                {l('Découvrir les plateformes accessibles')}
              </Button>
            </div>
          </div>

          {/* Flow diagram */}
          <div
            data-testid="publishers-flow-diagram"
            className="mt-14 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-4 bg-white/80 backdrop-blur-sm border border-blue-100 rounded-3xl p-6 md:p-8 shadow-sm"
          >
            <div className="text-center md:text-left">
              <MonitorSmartphone className="w-8 h-8 text-blue-600 mx-auto md:mx-0 mb-3" />
              <p className="font-semibold text-gray-900">{l('Votre site ou application')}</p>
            </div>
            <ArrowRight className="hidden md:block w-6 h-6 text-blue-400 mx-auto" />
            <ArrowDown className="md:hidden w-6 h-6 text-blue-400 mx-auto" />
            <div className="text-center rounded-2xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white py-6 px-4">
              <Plug className="w-8 h-8 mx-auto mb-3" />
              <p className="font-semibold">{l('API OptimHub')}</p>
            </div>
            <ArrowRight className="hidden md:block w-6 h-6 text-blue-400 mx-auto" />
            <ArrowDown className="md:hidden w-6 h-6 text-blue-400 mx-auto" />
            <div className="text-center md:text-right">
              <Layers className="w-8 h-8 text-cyan-600 mx-auto md:ml-auto md:mr-0 mb-3" />
              <p className="font-semibold text-gray-900">{l('Plusieurs plateformes de monétisation')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Platforms */}
      <section id="publisher-platforms" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <h2 className="text-base md:text-lg font-semibold uppercase tracking-widest text-blue-600 mb-4">
              {l('Plateformes accessibles')}
            </h2>
            <p className="text-3xl md:text-4xl font-bold text-gray-900 leading-snug mb-6">
              {l('Élargissez vos opportunités avec une seule connexion.')}
            </p>
            <p className="text-gray-600 leading-relaxed">
              {l("Via l'API OptimHub, vous accédez aux annonceurs et aux offres disponibles sur chacune de ces plateformes de monétisation. Les plateformes, les annonceurs et les conditions accessibles dépendent de votre activité et sont précisés lors de la prise de contact.")}
            </p>
          </div>

          <div
            data-testid="publishers-platforms-grid"
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4"
          >
            {platforms.map((platform) => (
              <div
                key={platform.name}
                data-testid={`publisher-platform-${platform.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="group h-28 rounded-2xl border border-gray-100 bg-gray-50/70 hover:bg-white hover:shadow-lg hover:border-blue-100 transition-all duration-300 flex flex-col items-center justify-center gap-2 p-4"
              >
                {platform.logo ? (
                  <img
                    src={platform.logo}
                    alt={platform.name}
                    loading="lazy"
                    className="max-h-8 max-w-[80%] object-contain grayscale group-hover:grayscale-0 transition-all duration-300"
                    onError={(event) => {
                      event.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center text-blue-700 font-bold">
                    {platform.name.charAt(0)}
                  </span>
                )}
                <span className="text-xs font-medium text-gray-600 text-center leading-tight">
                  {platform.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Benefits */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <h2 className="text-base md:text-lg font-semibold uppercase tracking-widest text-blue-600 mb-4">
              {l('Bénéfices')}
            </h2>
            <p className="text-3xl md:text-4xl font-bold text-gray-900 leading-snug">
              {l('Développez votre monétisation sans multiplier les intégrations.')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = icons[benefit.icon];
              return (
                <Card
                  key={benefit.title}
                  data-testid={`publisher-benefit-${index}`}
                  className="group border-0 bg-white hover:shadow-xl transition-all duration-500 hover:-translate-y-1"
                >
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center mb-6 group-hover:from-blue-500 group-hover:to-cyan-500 transition-all duration-300">
                      <Icon className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{l(benefit.title)}</h3>
                    <p className="text-gray-600 leading-relaxed">{l(benefit.description)}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. How it works */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <h2 className="text-base md:text-lg font-semibold uppercase tracking-widest text-blue-600 mb-4">
              {l('Fonctionnement')}
            </h2>
            <p className="text-3xl md:text-4xl font-bold text-gray-900 leading-snug">
              {l('De votre audience aux premières opportunités.')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {steps.map((step, index) => (
              <div key={step.title} data-testid={`publisher-step-${index}`}>
                <span className="text-sm font-semibold uppercase tracking-widest text-cyan-600">
                  {l(step.label)}
                </span>
                <h3 className="text-xl font-bold text-gray-900 mt-3 mb-3">{l(step.title)}</h3>
                <p className="text-gray-600 leading-relaxed">{l(step.description)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Compensation */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-start gap-8">
            <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0">
              <Coins className="w-8 h-8 text-cyan-400" />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold leading-snug mb-6">
                {l('Au clic ou à la conversion, selon les opportunités.')}
              </h2>
              <p className="text-lg text-gray-300 leading-relaxed" data-testid="publishers-compensation-text">
                {l("OptimHub propose des modèles CPC et CPA. Le mode de rémunération et les conditions applicables sont précisés avant l'activation des campagnes.")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Publisher profiles */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <h2 className="text-base md:text-lg font-semibold uppercase tracking-widest text-blue-600 mb-4">
              {l('Profils recherchés')}
            </h2>
            <p className="text-3xl md:text-4xl font-bold text-gray-900 leading-snug">
              {l("Les éditeurs et plateformes qui utilisent l'API OptimHub")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {publisherTypes.map((type, index) => {
              const Icon = icons[type.icon];
              return (
                <div
                  key={type.title}
                  data-testid={`publisher-type-${index}`}
                  className="rounded-2xl border border-gray-100 bg-gray-50/70 p-6 hover:bg-white hover:shadow-lg transition-all duration-300"
                >
                  <Icon className="w-6 h-6 text-blue-600 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{l(type.title)}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{l(type.description)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 7. Contact */}
      <section id="publisher-contact" className="py-20 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {l('Connectez votre audience à de nouvelles opportunités.')}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {l('Présentez-nous votre activité pour découvrir les plateformes et les possibilités de monétisation adaptées à votre audience.')}
            </p>
          </div>

          <form onSubmit={handleSubmit} data-testid="publisher-application-form" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelClass} htmlFor="publisher-name">{l('Nom')}</label>
                <input
                  id="publisher-name"
                  data-testid="publisher-input-name"
                  required
                  value={form.name}
                  onChange={handleChange('name')}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass} htmlFor="publisher-email">{l('Email professionnel')}</label>
                <input
                  id="publisher-email"
                  data-testid="publisher-input-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={handleChange('email')}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass} htmlFor="publisher-url">{l("URL du site ou de l'application")}</label>
                <input
                  id="publisher-url"
                  data-testid="publisher-input-site-url"
                  required
                  value={form.siteUrl}
                  onChange={handleChange('siteUrl')}
                  placeholder="https://"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass} htmlFor="publisher-type">{l('Type de publisher')}</label>
                <select
                  id="publisher-type"
                  data-testid="publisher-select-site-type"
                  required
                  value={form.siteType}
                  onChange={handleChange('siteType')}
                  className={inputClass}
                >
                  <option value="">{l('Sélectionnez votre profil')}</option>
                  {publisherTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>{l(option.label)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass} htmlFor="publisher-traffic">
                  {l('Trafic mensuel')} <span className="text-gray-400">({l('facultatif')})</span>
                </label>
                <select
                  id="publisher-traffic"
                  data-testid="publisher-select-traffic"
                  value={form.monthlyTraffic}
                  onChange={handleChange('monthlyTraffic')}
                  className={inputClass}
                >
                  <option value="">{l('Non précisé')}</option>
                  {trafficOptions.map((option) => (
                    <option key={option.value} value={option.value}>{l(option.label)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass} htmlFor="publisher-countries">
                  {l('Pays ou zones principales')} <span className="text-gray-400">({l('facultatif')})</span>
                </label>
                <input
                  id="publisher-countries"
                  data-testid="publisher-input-countries"
                  value={form.countries}
                  onChange={handleChange('countries')}
                  className={inputClass}
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass} htmlFor="publisher-verticals">
                  {l('Verticales principales')} <span className="text-gray-400">({l('facultatif')})</span>
                </label>
                <input
                  id="publisher-verticals"
                  data-testid="publisher-input-verticals"
                  value={form.verticals}
                  onChange={handleChange('verticals')}
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className={labelClass} htmlFor="publisher-message">
                {l('Message')} <span className="text-gray-400">({l('facultatif')})</span>
              </label>
              <textarea
                id="publisher-message"
                data-testid="publisher-input-message"
                value={form.message}
                onChange={handleChange('message')}
                rows={5}
                className={inputClass}
              />
            </div>

            {status.state === 'success' && (
              <div data-testid="publisher-form-success" className="rounded-lg border border-green-200 bg-green-50 p-4 text-green-800">
                {status.message}
              </div>
            )}
            {status.state === 'error' && (
              <div data-testid="publisher-form-error" className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
                {status.message}
              </div>
            )}

            <Button
              data-testid="publisher-form-submit"
              type="submit"
              disabled={status.state === 'loading'}
              className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-8 py-6 text-base rounded-full transition-all duration-300 transform hover:scale-105 disabled:opacity-60 disabled:transform-none"
            >
              {status.state === 'loading' ? l('Envoi en cours...') : l('Demander mon accès API')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PublishersPage;
